/**
 * EventProgress — the "Event Progress" card on the event detail page.
 *
 * Four stages on a rail, straight from the Figma, and they map one-for-one onto the
 * service's `EventLifecycleStage`, so nothing has to be invented or derived:
 *
 *   PLANNING · PRE_EVENT_EXECUTION · ON_SITE_EXECUTION · POST_EVENT_EXECUTION
 *
 * Two marker states: a green tick for every stage the event has reached — the current one
 * included — and a clock for stages not reached. The rail behind a reached stage is green,
 * the rest grey. The Figma also draws an "in progress" marker for the current stage; testing
 * feedback asked for the tick instead (a stage the marketer has moved the event to reads as
 * done), so the current stage is told apart only by `aria-current` and its announced state.
 *
 * `lifecycleStage` is optional on the wire. When it is missing nothing is claimed as
 * done — every marker reads "not started" — rather than defaulting to PLANNING, which
 * would assert a stage the service never sent.
 *
 * Read-only. Advancing a stage is an UpdateEvent call, made from the edit page's stage picker
 * (the Figma puts it there, in the page header), not here.
 */

import Box from '@amzn/awsui-components-react/polaris/box';
import Container from '@amzn/awsui-components-react/polaris/container';
import Header from '@amzn/awsui-components-react/polaris/header';
import Icon from '@amzn/awsui-components-react/polaris/icon';
import type { EventLifecycleStage } from '../../client/EventCalendarClient';
import { LIFECYCLE_STAGES } from '../../utils/eventTaxonomy';
import './EventProgress.css';

/** The stages in the order the event moves through them, as the Figma lays them out. */
const STAGES = LIFECYCLE_STAGES;

type MarkerState = 'done' | 'upcoming';

/**
 * Index of the current stage, or -1 when there is none.
 *
 * -1 rather than 0 for a missing stage: it makes every marker "upcoming", which is the
 * honest rendering of "the service did not say". Treating it as PLANNING would show a
 * stage as reached on no evidence.
 */
function currentIndex(stage: EventLifecycleStage | undefined): number {
  if (!stage) return -1;
  return STAGES.findIndex((s) => s.stage === stage);
}

/** Reached (the current stage and every one before it) or not. */
function markerFor(index: number, current: number): MarkerState {
  return current < 0 || index > current ? 'upcoming' : 'done';
}

/** Marker glyphs: tick / clock. */
const MARKER_ICON = {
  done: { name: 'status-positive', variant: 'success' },
  upcoming: { name: 'status-pending', variant: 'subtle' },
} as const;

export interface EventProgressProps {
  /** The event's stage. Undefined renders every marker as not started. */
  lifecycleStage: EventLifecycleStage | undefined;
}

export default function EventProgress({ lifecycleStage }: EventProgressProps) {
  const current = currentIndex(lifecycleStage);

  return (
    <Container header={<Header variant="h2">Event Progress</Header>}>
      <ol className="eh-progress" aria-label="Event progress">
        {STAGES.map(({ stage, label }, i) => {
          const marker = markerFor(i, current);
          const isCurrent = i === current;
          const icon = MARKER_ICON[marker];
          return (
            <li
              key={stage}
              className={`eh-progress__step eh-progress__step--${marker}`}
              /* The visual state is carried by an icon and a rail colour, neither of
                 which a screen reader conveys, so each step says where it stands. */
              aria-current={isCurrent ? 'step' : undefined}
            >
              <span className="eh-progress__rail" aria-hidden="true" />
              <span className="eh-progress__marker">
                <Icon name={icon.name} variant={icon.variant} />
              </span>
              <span className="eh-progress__label">
                <Box fontSize="body-s">{label}</Box>
                <span className="eh-progress__sr-state">
                  {isCurrent
                    ? ' — current stage'
                    : marker === 'done'
                      ? ' — completed'
                      : ' — not started'}
                </span>
              </span>
            </li>
          );
        })}
      </ol>
    </Container>
  );
}
